import { useMemo } from "react";
import { hasAvailableVariantsMatchingColour } from "@/utils/product/hasAvailableVariantsMatchingColour.ts";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly.ts";
import { isFutureRelease } from "@/utils/variant/isFutureRelease.ts";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder.ts";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive.ts";
import { useUserAcknowledgementSatisfied } from "@/components/AddToBasket/AddToBasketControls/hooks/useUserAcknowledgementSatisfied.ts";
import { Product, Variant, FeatureFlags } from "@/types/types";
import { ButtonState } from "@/components/AddToBasket/AddToBasketControls/types";

interface UseButtonStatesParams {
  product: Product;
  variant: Variant;
  isSelected: boolean;
  configProps: Record<string, unknown> | undefined;
  flags: FeatureFlags;
  isSubmitting: boolean;
  isExpressSubmitting: boolean;
  apiErrorState: ButtonState;
}

export type BaseButtonState = {
  visible: boolean;
  enabled: boolean;
};

export type AddToBasketButtonState = BaseButtonState & {
  text: string;
};

interface ButtonStates {
  addToBasket: AddToBasketButtonState;
  expressCheckout: BaseButtonState;
  areBasketButtonsVisible: boolean;
}

export const useButtonStates = ({
  product,
  variant,
  isSelected,
  configProps,
  flags,
  isSubmitting,
  isExpressSubmitting,
  apiErrorState,
}: UseButtonStatesParams): ButtonStates => {
  const isUserAcknowledgementSatisfied =
    useUserAcknowledgementSatisfied(product);

  return useMemo(() => {
    const activeProduct = ensureIsActive(product);
    const activeVariant = ensureIsActiveVariant(variant);

    const availableToOrder = !!activeVariant?.availability.availableToOrder;
    const isNotifiable =
      !availableToOrder &&
      (activeVariant?.customerNotifiableEvents.backInStock ||
        activeVariant?.customerNotifiableEvents.onRelease);

    const isInStoreOnlyVariant = isInStoreOnly(variant);
    const isFutureReleaseVariant = isFutureRelease(variant);
    const isPreOrderableVariant =
      activeVariant && isAvailableToPreOrder(activeVariant);
    const isSingleSkuProduct = product.variants.length === 1;

    let addToBasketVisibleByProduct = true;

    if (!activeProduct || !activeVariant) {
      addToBasketVisibleByProduct = false;
    } else if (!isUserAcknowledgementSatisfied) {
      addToBasketVisibleByProduct = false;
    } else if (
      isSelected &&
      (isInStoreOnlyVariant || isNotifiable || isFutureReleaseVariant)
    ) {
      addToBasketVisibleByProduct = false;
    } else if (
      isSingleSkuProduct &&
      !availableToOrder &&
      !isFutureReleaseVariant &&
      !isPreOrderableVariant
    ) {
      addToBasketVisibleByProduct = false;
    } else if (!availableToOrder && variant.id) {
      addToBasketVisibleByProduct = false;
    }

    const isShipFromStore =
      isSelected && activeVariant?.availability?.shipFromStore === true;

    const isAddToBasketDisabledByConfig =
      "addToBasketButtonEnabled" in (configProps || {})
        ? configProps?.addToBasketButtonEnabled === false
        : configProps?.addToBasketButtonDisabled === true;

    const addToBasketVisible =
      addToBasketVisibleByProduct &&
      !isAddToBasketDisabledByConfig &&
      !isShipFromStore;

    const expressCheckoutVisible =
      addToBasketVisibleByProduct &&
      (configProps?.expressCheckoutEnabled === true || isShipFromStore);

    const hasAvailableVariantsMatchingCurrentColour =
      hasAvailableVariantsMatchingColour(
        product,
        variant?.differentiators?.colour,
      );

    const enabledByProductAvailability = isSelected
      ? variant?.availability?.availableToOrder === true
      : hasAvailableVariantsMatchingCurrentColour;

    const { addToBasketDisabled, expressCheckoutDisabled, buttonText } =
      apiErrorState;

    const addToBasketEnabledBySubmissionState = !isExpressSubmitting;
    const expressCheckoutEnabledBySubmissionState = !isSubmitting;

    return {
      addToBasket: {
        visible: addToBasketVisible,
        enabled:
          enabledByProductAvailability &&
          !addToBasketDisabled &&
          addToBasketEnabledBySubmissionState,
        text: buttonText,
      },
      expressCheckout: {
        visible: expressCheckoutVisible,
        enabled:
          enabledByProductAvailability &&
          !expressCheckoutDisabled &&
          expressCheckoutEnabledBySubmissionState,
      },
      areBasketButtonsVisible: addToBasketVisible || expressCheckoutVisible,
    };
  }, [
    product,
    variant,
    isSelected,
    configProps,
    flags,
    isSubmitting,
    isExpressSubmitting,
    apiErrorState,
    isUserAcknowledgementSatisfied,
  ]);
};
